import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "./Sidebar/Sidebar";

const LINE_COLOR = "#F5F4F0";

function BurgerLines({ open }) {
  return (
    <div className="relative w-7 h-[17px] flex-shrink-0">
      {/* top line */}
      <motion.span
        className="block h-px absolute left-0 right-0"
        animate={open
          ? { top: "8px", rotate: 45, width: "28px" }
          : { top: "0px", rotate: 0, width: "28px" }
        }
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{ backgroundColor: LINE_COLOR, transformOrigin: "center" }}
      />
      {/* middle line — fades out when open */}
      <motion.span
        className="block h-px absolute left-0"
        animate={open
          ? { top: "8px", opacity: 0, width: "28px" }
          : { top: "8px", opacity: 1, width: "22px" }
        }
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        style={{ backgroundColor: LINE_COLOR }}
      />
      {/* bottom line */}
      <motion.span
        className="block h-px absolute left-0"
        animate={open
          ? { top: "8px", rotate: -45, width: "28px" }
          : { top: "17px", rotate: 0, width: "18px" }
        }
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{ backgroundColor: LINE_COLOR, transformOrigin: "center" }}
      />
    </div>
  );
}

export default function Navbar({ lenis }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const burgerRef = useRef(null);

  /* collapse to pill after scrolling past hero */
  useEffect(() => {
    const heroEl = document.querySelector("#hero");
    let threshold = heroEl ? heroEl.offsetHeight * 0.6 : window.innerHeight * 0.6;
    const refreshThreshold = () => {
      threshold = heroEl ? heroEl.offsetHeight * 0.6 : window.innerHeight * 0.6;
    };
    window.addEventListener("resize", refreshThreshold, { passive: true });
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", refreshThreshold);
    };
  }, []);

  /* close drawer on scroll */
  useEffect(() => {
    if (!menuOpen) return;
    const onScroll = () => setMenuOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [menuOpen]);

  const scrollTo = (id) => {
    setMenuOpen(false);
    setTimeout(() => {
      const el = document.querySelector(id);
      if (el) lenis.scrollTo(el, { offset: -60, duration: 1.6, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    }, 300);
  };

  const toggle = () => setMenuOpen((v) => !v);

  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        {!scrolled ? (
          /* ── Full navbar (hero) ── */
          <motion.header
            key="full-nav"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 left-0 right-0 z-50 px-6 md:px-10 py-5 flex items-center justify-end"
          >
            <div className="flex items-center gap-6">
              <a
                href="mailto:aneesazhar1993@gmail.com"
                className="hidden md:block font-Montreal-Mono text-[11px] tracking-widest uppercase hover:opacity-100 transition-opacity duration-200"
                style={{ color: "rgba(245,244,240,0.45)" }}
              >
                Let's talk
              </a>
              <button
                ref={burgerRef}
                onClick={toggle}
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                aria-expanded={menuOpen}
                className="flex flex-col gap-[5px] items-end"
              >
                <BurgerLines open={menuOpen} />
              </button>
            </div>
          </motion.header>
        ) : (
          /* ── Collapsed pill (scrolled) ── */
          <motion.div
            key="pill-nav"
            initial={{ opacity: 0, scale: 0.85, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-4 right-6 md:right-10 z-50"
          >
            <button
              ref={burgerRef}
              onClick={toggle}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="flex items-center gap-3 px-4 py-3 rounded-full"
              style={{
                background: "rgba(245,244,240,0.07)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: "1px solid rgba(245,244,240,0.12)",
              }}
            >
              <span
                className="font-Montreal-Mono text-[10px] tracking-widest uppercase"
                style={{ color: "rgba(245,244,240,0.7)" }}
              >
                {menuOpen ? "Close" : "Menu"}
              </span>
              <BurgerLines open={menuOpen} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <Sidebar
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        scrollTo={scrollTo}
        anchorRef={burgerRef}
      />
    </>
  );
}
